/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { CommentsProvider } from '@components/editors/shared/comments/comments-context.tsx';
import { useCommentsContext } from '@components/editors/shared/comments/comments-context.tsx';
import { CommentsGutter } from '@components/editors/shared/comments/comments-gutter.tsx';
import { JumpToViewWord } from '@components/editors/jump-to-view-word.tsx';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { getCurrentParagraphSeeking } from '@utils/editors/get-current-paragraph-seeking.ts';
import { indexOf } from 'lodash';
import { TranscriptRow } from '@components/editors/transcript-editor/body/transcript-editor-row.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { TranscriptTextStyleBox } from '@components/editors/transcript-editor/body/transcript-text-style-box.tsx';
import { EditorHistoryTools } from '@components/editors/shared/editor-history-tools.tsx';

type TranscriptBodyProps = {
  sessionId: string;
  documentId: string;
  enabled: boolean;
  /** Render only the editor area without side panels (for split view layout) */
  editorOnly?: boolean;
  /**
   * Lock the Slate surface in every row. Used by the split-view source
   * pane so the original transcript can't be edited while the translator
   * works on the right side (#2226 split-view acceptance line).
   */
  readOnly?: boolean;
  /** Expose Virtuoso scroller element for scroll sync */
  sourceScrollerRef?: React.MutableRefObject<HTMLElement | null>;
};

/**
 * Virtuoso row wrapper. Only reserves gutter space on wide screens
 * when there are actually comments (or an active compose draft) in
 * this document — otherwise the editor takes the full width and no
 * dead strip sits on the right (#2244 follow-up: "when the transcript
 * has no comments the gutter space just sits empty").
 */
function VirtuosoItem({
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const ctx = useCommentsContext();
  const hasGutter = !!ctx && (ctx.allComments.length > 0 || ctx.draft != null);
  return (
    <div
      {...props}
      className={cn('pr-3', hasGutter && 'lg:pr-[336px]')}
      style={{
        paddingTop: '0.75rem',
        paddingBottom: '0.15rem',
        paddingLeft: '0.75rem'
      }}
    >
      {children}
    </div>
  );
}

export const TranscriptBody = ({
  sessionId,
  documentId,
  enabled: _enabled,
  editorOnly,
  readOnly,
  sourceScrollerRef
}: TranscriptBodyProps) => {
  void _enabled;
  const { currentTime } = useAudio();
  const {
    document: td,
    setShowJumpButton,
    showJumpButton,
    popoverOpen,
    setPopoverOpen,
    documentVersion,
    undo,
    redo,
    undoStack,
    redoStack,
    saveDocument,
    saveDocumentLoading
  } = useTranscript();
  const { t } = useTranslation();

  const currentWordRef = useRef<HTMLSpanElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Scroller element used as the comments-gutter's coordinate space.
  // Shared across the editorOnly / normal branches so the gutter can
  // query word anchors by `data-start`. In editorOnly mode we also
  // forward it to the parent's `sourceScrollerRef` so scroll sync
  // keeps working. (#2244)
  const gutterScrollerRef = useRef<HTMLElement | null>(null);

  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();

  useEffect(() => {
    if (popoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [popoverOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setPopoverOpen(false);
    }
  };

  const onWordOutOfView = (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => {
    currentWordRef.current = ref;
    setShowJumpButton(direction);
  };

  const onWordInView = () => {
    setShowJumpButton(false);
  };

  const getEditorValue = useMemo(() => {
    if (!td || !td.document) return [];
    return td.document.map((paragraph, index) => ({
      ...paragraph,
      id: index
    }));
  }, [td]);

  const jumpToCurrentWord = useCallback(() => {
    const item = getCurrentParagraphSeeking(getEditorValue, currentTime);

    if (item && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: indexOf(getEditorValue, item),
        align: 'center',
        behavior: 'smooth'
      });
    }
    setShowJumpButton(false);
  }, [getEditorValue, currentTime]);

  const renderRow = useCallback(
    (index: number) => {
      const paragraph = getEditorValue[index];
      return (
        <TranscriptRow
          key={`row-v${documentVersion}-${paragraph.start}-${paragraph.stop}`}
          index={index}
          paragraph={paragraph}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          readOnly={readOnly}
        />
      );
    },
    [getEditorValue, onWordOutOfView, onWordInView, documentVersion, readOnly]
  );

  const vComponents: VirtuosoComponents = useMemo(
    () => ({
      Item: VirtuosoItem
    }),
    []
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const { start, stop } = (e as CustomEvent).detail || {};
      if (start === undefined || stop === undefined) return;

      const idx = getEditorValue.findIndex(
        p => (p.start ?? 0) <= start && (p.stop ?? 0) >= stop
      );
      if (idx >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: idx,
          align: 'center',
          behavior: 'smooth'
        });
      }
    };

    window.addEventListener('transcript:focusMatch', handler as EventListener);
    return () =>
      window.removeEventListener(
        'transcript:focusMatch',
        handler as EventListener
      );
  }, [getEditorValue]);

  // Editor-only mode: render just the editor area (for split view — inside grid)
  // Capture Virtuoso scroller element for scroll sync
  const handleSourceScrollerRef = useCallback(
    (el: HTMLElement | Window | null) => {
      if (el && el instanceof HTMLElement) {
        gutterScrollerRef.current = el;
        if (sourceScrollerRef) sourceScrollerRef.current = el;
      }
    },
    [sourceScrollerRef]
  );

  const handleGutterScrollerRef = useCallback(
    (el: HTMLElement | Window | null) => {
      if (el && el instanceof HTMLElement) gutterScrollerRef.current = el;
    },
    []
  );

  if (editorOnly) {
    return (
      // EditorProvider is hoisted to transcript-main so the Find &
      // Replace popover (rendered as a sibling of TranscriptBody) can
      // share `state.editors` with the paragraph rows. See the comment
      // on the hoisted provider; the fix for Replace All silently
      // doing nothing in the new popover (#2290).
      <CommentsProvider
        sessionId={sessionId}
        documentId={documentId}
        readOnly={readOnly}
        documentUtterances={td?.document}
      >
        <div
          className={cn('h-full flex flex-col', cls.pageBg, cls.text)}
          style={vars}
        >
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <section className="flex flex-col h-full min-h-0">
              {/* One rounded card wrapping header + editor — matches
                    the main-editor chrome so both split panes read as a
                    single surface. */}
              <div
                className="relative flex-1 min-h-0 m-2 flex flex-col rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                {readOnly && (
                  <header
                    className="flex items-center justify-between gap-2 px-3 py-1 h-11 shrink-0 border-b"
                    style={{ borderColor: getColour('border') }}
                  >
                    <span
                      className="text-xs font-semibold uppercase tracking-wider"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {t('original')}
                    </span>
                    <span
                      className="text-[11px] px-1.5 py-0.5 rounded-full border"
                      style={{
                        borderColor: getColour('border'),
                        color: getColour('inactiveText'),
                        backgroundColor: getColour('secondLayer')
                      }}
                    >
                      {t('read-only')}
                    </span>
                  </header>
                )}
                <div className="relative flex-1 min-h-0 text-lg overflow-auto">
                  <Virtuoso
                    ref={virtuosoRef}
                    totalCount={getEditorValue.length}
                    style={{
                      height: '100%',
                      width: '100%',
                      overflowX: 'hidden'
                    }}
                    components={vComponents}
                    itemContent={renderRow}
                    initialItemCount={Math.min(getEditorValue.length, 20)}
                    increaseViewportBy={3000}
                    scrollerRef={handleSourceScrollerRef}
                  />
                  <CommentsGutter scrollRootRef={gutterScrollerRef} />
                </div>
              </div>

              <JumpToViewWord
                onJumpToWord={jumpToCurrentWord}
                visible={showJumpButton}
              />
            </section>
          </div>
        </div>
      </CommentsProvider>
    );
  }

  // Normal mode: editor at full width. Comments render as anchored
  // flyout cards in `CommentsGutter`, overlaid on the right edge of
  // the Virtuoso scroller so the editor keeps its full width (#2244).
  // EditorProvider is hoisted to transcript-main (see editorOnly
  // branch comment above).
  return (
    <CommentsProvider
      sessionId={sessionId}
      documentId={documentId}
      documentUtterances={td?.document}
    >
      <div
        className={cn('h-full flex flex-col px-4', cls.pageBg, cls.text)}
        style={vars}
      >
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          <div className="flex-1 min-h-0 overflow-hidden">
            <section
              aria-labelledby="section-1-title"
              className="flex flex-col h-full"
            >
              {/* Header + editor viewport as one card — a single 1px border
                  wraps both, with a hairline divider between them (no
                  double border between the header and the Virtuoso list). */}
              <div
                className="relative flex-1 min-h-0 m-2 flex flex-col rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]"
                style={{
                  borderColor: getColour('border'),
                  backgroundColor: getColour('secondLayer')
                }}
              >
                {/* Inline body header — undo/redo/save on the left,
                      formatting toolbox on the right (#2289 moved the
                      edit-history icons here from the top EditorHeader
                      so they sit right next to the content they
                      affect). */}
                <header
                  className="flex items-center justify-between gap-2 px-3 py-1 h-12 shrink-0 border-b"
                  style={{ borderColor: getColour('borderSoft') }}
                >
                  <EditorHistoryTools
                    undo={undo}
                    redo={redo}
                    save={() => saveDocument(true)}
                    undoDisabled={saveDocumentLoading || undoStack.length === 0}
                    redoDisabled={saveDocumentLoading || redoStack.length === 0}
                    saveDisabled={saveDocumentLoading}
                    saving={saveDocumentLoading}
                    undoLabel={t('undo')}
                    redoLabel={t('redo')}
                    saveLabel={t('save')}
                  />
                  <TranscriptTextStyleBox
                    containerRef={containerRef}
                    popoverOpen={popoverOpen}
                    sessionId={sessionId}
                    documentId={documentId}
                  />
                </header>

                <div className="relative flex-1 text-lg overflow-auto">
                  <Virtuoso
                    ref={virtuosoRef}
                    totalCount={getEditorValue.length}
                    style={{
                      height: '100%',
                      width: '100%',
                      overflowX: 'hidden'
                    }}
                    components={vComponents}
                    itemContent={renderRow}
                    // Cap initial synchronous mount — each row is a full Slate
                    // editor; passing the whole length mounted every editor at once
                    // and froze/OOM-killed mobile on large docs. (See caption-main.tsx.)
                    initialItemCount={Math.min(getEditorValue.length, 20)}
                    // Wide viewport buffer so a paragraph the user is actively
                    // editing doesn't get unmounted during a normal trackpad
                    // scroll — otherwise Virtuoso throws away Slate's per-row
                    // selection state, the caret vanishes, and the next click
                    // lands on a fresh editor that can't restore the position
                    // the user was at (Ingrid/Martijn trackpad regression).
                    increaseViewportBy={3000}
                    scrollerRef={handleGutterScrollerRef}
                  />
                  <CommentsGutter scrollRootRef={gutterScrollerRef} />
                </div>
              </div>

              <JumpToViewWord
                onJumpToWord={jumpToCurrentWord}
                visible={showJumpButton}
              />
            </section>
          </div>
        </div>
      </div>
    </CommentsProvider>
  );
};
